import { useState, useEffect, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Download, FileSpreadsheet, Eye } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import FiltroColumna from "../../components/FiltroColumna";
import { exportarReporte, fechaReporte } from "../../utils/reportes";

/* Fix EE (2026-09-24): certificado de un contrato con terceros, POR TAREA (Edson, 23/09):
   "Tenemos que poder imputar montos o porcentajes a cada tarea y la sumatoria de todas
   las tareas debe ser el total del monto a pagar… queremos tener trazabilidad de los
   certificados anteriores: columnas de Anterior, Actual, Acumulado en porcentaje y monto…
   que se cargue manualmente el % actual y de ahí salga el Gs Actual… que cada certificado
   se pueda descargar en PDF o Excel… filtros tipo Google Sheets en cada encabezado".
   Sin `certId` es un certificado nuevo (se carga el % actual); con `certId` se ve uno ya
   cargado. Sin montos (Residente) solo se ven y cargan porcentajes. */
const opts = { withCredentials: true };
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const gs = (v) => (v == null ? "—" : Math.round(Number(v) || 0).toLocaleString("es-PY"));
const pct = (v) => (v == null ? "—" : `${r2(v).toLocaleString("es-PY", { maximumFractionDigits: 2 })}%`);
const numIn = (s) => Number(String(s ?? "").replace(",", ".")) || 0;

export default function CertificadoContratoModal({ obraId, contrato, certId = null, canMoney, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [pcts, setPcts] = useState({});
  const [obs, setObs] = useState({});
  const [orden, setOrden] = useState(null);        // { k, dir }
  const [filtros, setFiltros] = useState({});      // { k: Set }
  const [todas, setTodas] = useState("");
  const [saving, setSaving] = useState(false);
  const [exportando, setExportando] = useState("");
  const editable = !certId;

  useEffect(() => {
    axios.get(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}/planilla${certId ? `?cert_id=${certId}` : ""}`, opts)
      .then(({ data }) => setData(data))
      .catch(e => { toast.error(e.response?.data?.detail || "No se pudo cargar la planilla"); onClose(); });
  }, [obraId, contrato.id, certId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filas = useMemo(() => (data?.filas || []).map(f => {
    const pc = editable ? numIn(pcts[f.tarea_id]) : (Number(f.pct_actual) || 0);
    const gc = canMoney ? (editable ? r2((Number(f.monto_cerrado) || 0) * pc / 100) : (Number(f.monto_actual) || 0)) : null;
    return {
      ...f, pc, pac: r2((Number(f.pct_anterior) || 0) + pc), gc,
      gac: canMoney ? r2((Number(f.monto_anterior) || 0) + gc) : null,
      obsTxt: editable ? (obs[f.tarea_id] ?? "") : (f.observaciones || ""),
      invalido: pc < 0 || (Number(f.pct_anterior) || 0) + pc > 100.01,
    };
  }), [data, pcts, obs, editable, canMoney]);

  const COLS = useMemo(() => [
    { k: "tarea", t: "Ítem / Tarea", tipo: "texto", v: f => `${f.item_numero ? f.item_numero + " " : ""}${f.tarea_nombre || ""}` },
    { k: "nivel", t: "Nivel", tipo: "texto", v: f => f.nivel_label || "—" },
    { k: "fondo", t: "Fondo M.O.", tipo: "gs", money: true, v: f => f.fondo_mo },
    { k: "consumido", t: "Consumido", tipo: "gs", money: true, v: f => f.consumido },
    { k: "saldo", t: "Saldo", tipo: "gs", money: true, v: f => f.saldo },
    { k: "contrato", t: "Contrato", tipo: "gs", money: true, v: f => f.monto_cerrado },
    { k: "pa", t: "% Anterior", tipo: "pct", v: f => f.pct_anterior },
    { k: "pc", t: "% Actual", tipo: "pct", v: f => f.pc },
    { k: "pac", t: "% Acumulado", tipo: "pct", v: f => f.pac },
    { k: "ga", t: "Gs Anterior", tipo: "gs", money: true, v: f => f.monto_anterior },
    { k: "gc", t: "Gs Actual", tipo: "gs", money: true, v: f => f.gc },
    { k: "gac", t: "Gs Acumulado", tipo: "gs", money: true, v: f => f.gac },
    { k: "var", t: "% Var", tipo: "pct", v: f => f.pct_var },
    { k: "obs", t: "Observaciones", tipo: "texto", v: f => f.obsTxt || "" },
  ].filter(c => canMoney || !c.money), [canMoney]);
  const texto = (c, f) => (c.tipo === "gs" ? gs(c.v(f)) : c.tipo === "pct" ? pct(c.v(f)) : String(c.v(f) ?? ""));

  const valoresCol = useMemo(() => {
    const out = {};
    COLS.forEach(c => {
      const vals = [...new Set(filas.map(f => texto(c, f)))];
      out[c.k] = c.tipo === "texto" ? vals.sort((a, b) => a.localeCompare(b, "es", { numeric: true }))
        : vals.sort((a, b) => numIn(a.replace(/\./g, "").replace("%", "")) - numIn(b.replace(/\./g, "").replace("%", "")));
    });
    return out;
  }, [COLS, filas]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibles = useMemo(() => {
    let out = filas.filter(f => COLS.every(c => !(filtros[c.k] && filtros[c.k].size) || filtros[c.k].has(texto(c, f))));
    if (orden) {
      const c = COLS.find(x => x.k === orden.k);
      if (c) {
        const s = orden.dir === "asc" ? 1 : -1;
        out = [...out].sort((a, b) => (c.tipo === "texto"
          ? texto(c, a).localeCompare(texto(c, b), "es", { numeric: true })
          : (Number(c.v(a)) || 0) - (Number(c.v(b)) || 0)) * s);
      }
    }
    return out;
  }, [filas, filtros, orden, COLS]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sin orden elegido: agrupado por sección e ítem, como la hoja de Tareas.
  const grupos = useMemo(() => {
    if (orden) return null;
    const porSeccion = new Map();
    visibles.forEach(f => {
      const s = f.item_seccion || "";
      if (!porSeccion.has(s)) porSeccion.set(s, new Map());
      const k = f.item_uid || `${f.item_numero}|${f.item_descripcion}`;
      const items = porSeccion.get(s);
      if (!items.has(k)) items.set(k, []);
      items.get(k).push(f);
    });
    return porSeccion;
  }, [visibles, orden]);

  const tot = useMemo(() => {
    const s = (k) => visibles.reduce((a, f) => a + (Number(f[k]) || 0), 0);
    const contratoT = s("monto_cerrado"), ga = s("monto_anterior"), gc = s("gc"), gac = s("gac");
    const p = (m) => (contratoT ? r2(m / contratoT * 100) : 0);
    return { fondo: s("fondo_mo"), consumido: s("consumido"), saldo: s("saldo"), contrato: contratoT, ga, gc, gac, pa: p(ga), pc: p(gc), pac: p(gac) };
  }, [visibles]);
  const totTexto = (c) => {
    if (!canMoney) return "";
    return { fondo: gs(tot.fondo), consumido: gs(tot.consumido), saldo: gs(tot.saldo), contrato: gs(tot.contrato),
      pa: pct(tot.pa), pc: pct(tot.pc), pac: pct(tot.pac), ga: gs(tot.ga), gc: gs(tot.gc), gac: gs(tot.gac) }[c.k] || "";
  };

  const aplicarTodas = () => {
    const v = String(todas).trim();
    if (v === "") return;
    setPcts(prev => { const n = { ...prev }; visibles.forEach(f => { n[f.tarea_id] = v; }); return n; });
  };

  const guardar = async () => {
    if (filas.some(f => f.invalido)) { toast.error("Hay tareas que pasan el 100% acumulado (en rojo)"); return; }
    const tareas = filas.filter(f => f.pc > 0 || (f.obsTxt || "").trim())
      .map(f => ({ tarea_id: f.tarea_id, pct_actual: f.pc, observaciones: f.obsTxt || "" }));
    if (!tareas.some(t => t.pct_actual > 0)) { toast.error("Cargá el % actual de al menos una tarea"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/obras/${obraId}/contratos-terceros/${contrato.id}/certificaciones`, { tareas }, opts);
      toast.success(`Certificado N° ${data.siguiente_numero} guardado como borrador`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar el certificado"); }
    finally { setSaving(false); }
  };

  const numero = data?.cert?.numero ?? data?.siguiente_numero;
  const contratista = data?.contrato?.contratista?.nombre || contrato.contratista?.nombre || "";
  const exportar = (tipo) => exportarReporte(tipo, {
    titulo: `Certificado N° ${numero} — ${contratista}${data?.contrato?.adenda_numero ? ` (Adenda ${data.contrato.adenda_numero})` : ""}`,
    filtros: [`Obra ${data?.obra?.codigo || ""} ${data?.obra?.nombre || ""}`.trim(),
      data?.cert?.fecha ? `Fecha ${fechaReporte(data.cert.fecha)}` : "",
      data?.cert?.estado === "validado" ? `Validado — ${data.cert.payment_order_number || ""}` : "Borrador"].filter(Boolean).join(" · "),
    columnas: COLS.map(c => ({ l: c.t, num: c.tipo === "gs" })),
    filas: visibles.map(f => COLS.map(c => (c.k === "tarea"
      ? `${f.item_numero ? f.item_numero + " " : ""}${f.item_descripcion || ""}${f.tarea_nombre && f.tarea_nombre !== f.item_descripcion ? ` — ${f.tarea_nombre}` : ""}`
      : c.tipo === "gs" ? c.v(f) : texto(c, f)))),
    totales: COLS.map((c, i) => (i === 0 ? "Totales" : c.tipo === "gs" ? { fondo: tot.fondo, consumido: tot.consumido, saldo: tot.saldo, contrato: tot.contrato, ga: tot.ga, gc: tot.gc, gac: tot.gac }[c.k] : totTexto(c))),
    archivo: `Certificado_${numero}_${contratista}`.replace(/[^A-Za-z0-9_]+/g, "_"),
  }, setExportando);

  const celda = (c, f) => {
    if (editable && c.k === "pc") {
      return (
        <input value={pcts[f.tarea_id] ?? ""} onChange={e => setPcts(p => ({ ...p, [f.tarea_id]: e.target.value }))} inputMode="decimal" placeholder="0"
          className={`w-16 border rounded px-1 py-0.5 text-right ${f.invalido ? "border-red-400 bg-red-50 text-red-700" : "border-gray-200"}`}
          data-testid={`cert-pct-${f.tarea_id}`} />
      );
    }
    if (editable && c.k === "obs") {
      return <input value={obs[f.tarea_id] ?? ""} onChange={e => setObs(p => ({ ...p, [f.tarea_id]: e.target.value }))} className="w-40 border border-gray-200 rounded px-1 py-0.5" data-testid={`cert-obs-${f.tarea_id}`} />;
    }
    if (c.k === "tarea") {
      return orden
        ? <span>{f.item_numero && <span className="font-semibold text-[#2E6DA4]">{f.item_numero} </span>}{f.tarea_nombre}{!f.en_obra && <span className="text-red-600"> (ya no está en la obra)</span>}</span>
        : <span className="pl-3 block">{f.tarea_nombre}{!f.en_obra && <span className="text-red-600"> (ya no está en la obra)</span>}</span>;
    }
    const t = texto(c, f);
    if (c.k === "pac" && f.pac > 100.01) return <span className="text-red-600 font-bold">{t}</span>;
    if (c.k === "saldo" && Number(f.saldo) < 0) return <span className="text-red-600 font-bold">{t}</span>;
    return t;
  };
  const fila = (f) => (
    <tr key={f.tarea_id} className="hover:bg-gray-50/60 border-t border-gray-50" data-testid={`cert-fila-${f.tarea_id}`}>
      {COLS.map(c => <td key={c.k} className={`px-2 py-1.5 ${c.tipo === "texto" ? "text-left" : "text-right"} ${c.k === "gc" || c.k === "pc" ? "bg-orange-50/40 font-semibold" : ""}`}>{celda(c, f)}</td>)}
    </tr>
  );

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3" data-testid="cert-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[96vw] max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-bold text-gray-900" data-testid="cert-titulo">
              {editable ? "Nuevo certificado" : "Certificado"} N° {numero ?? "…"} — {contratista}
              {data?.contrato?.adenda_numero ? <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Adenda {data.contrato.adenda_numero}</span> : null}
            </h2>
            <p className="text-xs text-gray-500">
              {data?.obra?.codigo} {data?.obra?.nombre}
              {data?.cert ? ` · ${fechaReporte(data.cert.fecha)} · ${data.cert.estado === "validado" ? `Validado — ${data.cert.payment_order_number || ""}` : "Borrador"}` : " · Cargá el % actual de cada tarea; el Gs actual sale solo"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!editable && (
              <>
                <button onClick={() => exportar("pdf")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="cert-pdf">
                  {exportando === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
                </button>
                <button onClick={() => exportar("excel")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="cert-excel">
                  {exportando === "excel" ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />} Excel
                </button>
                <button onClick={() => exportar("ver")} disabled={!!exportando || !data} className="flex items-center gap-1 text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50" data-testid="cert-preview">
                  <Eye size={13} /> Vista previa
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="cert-cerrar"><X size={17} className="text-gray-500" /></button>
          </div>
        </div>
        {!data ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div> : (
          <>
            <div className="px-5 py-2 flex items-center gap-3 flex-wrap text-xs border-b border-gray-100">
              <span className="text-gray-500">{visibles.length} de {filas.length} tarea(s)</span>
              {(orden || Object.values(filtros).some(s => s && s.size)) && (
                <button onClick={() => { setOrden(null); setFiltros({}); }} className="text-[#2E6DA4] font-semibold hover:underline" data-testid="cert-limpiar">Quitar orden y filtros</button>
              )}
              {editable && (
                <span className="flex items-center gap-1.5 ml-auto">
                  % actual para las tareas visibles:
                  <input value={todas} onChange={e => setTodas(e.target.value)} inputMode="decimal" className="w-16 border border-gray-200 rounded px-1 py-0.5 text-right" data-testid="cert-todas" />
                  <button onClick={aplicarTodas} className="font-semibold text-[#EA580C] hover:underline" data-testid="cert-todas-aplicar">Aplicar</button>
                </span>
              )}
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-xs" data-testid="cert-tabla">
                <thead className="sticky top-0 z-20 [&_th]:sticky [&_th]:top-0 [&_th]:bg-gray-50">
                  <tr className="text-[10px] text-gray-500 border-b border-gray-100">
                    {COLS.map(c => (
                      <th key={c.k} className={`px-2 py-2 whitespace-nowrap ${c.tipo === "texto" ? "text-left" : "text-right"}`}>
                        <FiltroColumna titulo={c.t} valores={valoresCol[c.k] || []} seleccion={filtros[c.k] || new Set()}
                          orden={orden?.k === c.k ? orden.dir : null} onOrden={dir => setOrden(dir ? { k: c.k, dir } : null)}
                          onAplicar={s => setFiltros(p => ({ ...p, [c.k]: s }))} alinear={c.tipo === "texto" ? "left" : "right"}
                          testId={`cert-col-${c.k}`} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibles.length === 0 && <tr><td colSpan={COLS.length} className="py-8 text-center text-gray-400">Sin tareas con esos filtros</td></tr>}
                  {grupos ? [...grupos.entries()].map(([seccion, items]) => (
                    <Fragment key={`s-${seccion}`}>
                      <tr className="bg-[#1A4B7A]"><td colSpan={COLS.length} className="px-2 py-1.5 font-bold text-white uppercase text-[11px]">{seccion || "Sin sección"} · {items.size} ítem{items.size !== 1 ? "s" : ""}</td></tr>
                      {[...items.entries()].map(([k, rows]) => (
                        <Fragment key={`i-${k}`}>
                          <tr className="bg-[#EBF3FB]"><td colSpan={COLS.length} className="px-2 py-1 font-bold text-[#2E6DA4]">{rows[0].item_numero} — {rows[0].item_descripcion}</td></tr>
                          {rows.map(fila)}
                        </Fragment>
                      ))}
                    </Fragment>
                  )) : visibles.map(fila)}
                </tbody>
                <tfoot className="sticky bottom-0 z-20 [&_td]:bg-[#FFF7ED]">
                  <tr className="font-bold text-gray-700" data-testid="cert-totales">
                    {COLS.map((c, i) => <td key={c.k} className={`px-2 py-2 ${c.tipo === "texto" ? "text-left" : "text-right"}`} data-testid={`cert-total-${c.k}`}>{i === 0 ? "Totales de lo visible" : totTexto(c)}</td>)}
                  </tr>
                </tfoot>
              </table>
            </div>
            {editable && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-gray-600" data-testid="cert-resumen">
                  {canMoney ? <>Total del certificado: <strong>Gs. {gs(filas.reduce((a, f) => a + (f.gc || 0), 0))}</strong></> : <>{filas.filter(f => f.pc > 0).length} tarea(s) con avance</>}
                  {filas.some(f => f.invalido) && <span className="text-red-600 font-semibold ml-2">Hay tareas que pasan el 100% acumulado</span>}
                </span>
                <div className="flex gap-2">
                  <button onClick={onClose} className="border border-gray-300 text-gray-700 rounded-xl px-4 py-2 text-xs font-medium hover:bg-gray-50">Cancelar</button>
                  <button onClick={guardar} disabled={saving} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl px-4 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60" data-testid="cert-guardar">
                    {saving && <Loader2 size={13} className="animate-spin" />} Guardar certificado
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
